package com.tovtalk.presentation.viewmodel.chat

import com.tovtalk.presentation.ViewModel
import com.tovtalk.presentation.ScopedViewModel
import com.tovtalk.domain.entity.*
import com.tovtalk.domain.repository.ChatRepository
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

/**
 * Chat ViewModel - דף שיחה
 */
class ChatViewModel(
    private val chatRepository: ChatRepository,
    private val userRepository: com.tovtalk.domain.repository.UserRepository,
    private val scope: CoroutineScope
) : ViewModel(), ScopedViewModel {

    /** Cancels the injected scope + messagesJob/typingJob/initChat/markAsRead when the screen leaves. */
    override fun dispose() {
        scope.cancel()
    }
    
    private val _messages = MutableStateFlow<UiState<List<Message>>>(UiState.Idle)
    val messages: StateFlow<UiState<List<Message>>> = _messages.asStateFlow()
    
    private val _isOtherTyping = MutableStateFlow(false)
    val isOtherTyping: StateFlow<Boolean> = _isOtherTyping.asStateFlow()

    private val _receiverProfile = MutableStateFlow<User?>(null)
    val receiverProfile: StateFlow<User?> = _receiverProfile.asStateFlow()

    private val _currentUser = MutableStateFlow<User?>(null)
    val currentUser: StateFlow<User?> = _currentUser.asStateFlow()

    private val _currentUserId = MutableStateFlow<Int?>(null)
    val currentUserId: StateFlow<Int?> = _currentUserId.asStateFlow()

    val currentUserIsReadOnly: StateFlow<Boolean> = _currentUser.map { it?.isReadOnly == true }.stateIn(scope, SharingStarted.WhileSubscribed(5000), false)

    /** Error from last send attempt (e.g. "Chat not yet implemented on iOS"). Clear when user sends again. */
    private val _sendMessageError = MutableStateFlow<String?>(null)
    val sendMessageError: StateFlow<String?> = _sendMessageError.asStateFlow()

    private val _showKashrutDialog = MutableStateFlow(false)
    val showKashrutDialog: StateFlow<Boolean> = _showKashrutDialog.asStateFlow()
    private val _kashrutDialogMessage = MutableStateFlow<String?>(null)
    val kashrutDialogMessage: StateFlow<String?> = _kashrutDialogMessage.asStateFlow()

    private val _showUserDoesNotAcceptMessagesDialog = MutableStateFlow(false)
    val showUserDoesNotAcceptMessagesDialog: StateFlow<Boolean> = _showUserDoesNotAcceptMessagesDialog.asStateFlow()

    /** View-only user can send only to non-view-only users. */
    val canSendToReceiver: StateFlow<Boolean> = combine(
        _currentUser,
        _receiverProfile
    ) { cur, rec ->
        when {
            cur?.isReadOnly != true -> true
            rec?.isReadOnly != true -> true
            else -> false
        }
    }.stateIn(scope, SharingStarted.WhileSubscribed(5000), true)

    private var currentConversationId: String? = null
    private var messagesJob: Job? = null
    private var typingJob: Job? = null

    fun initChat(receiverId: Int) {
        scope.launch {
            val currentUser = (userRepository.getCurrentUser() as? com.tovtalk.domain.entity.DomainResult.Success)?.data
            currentUser?.let { me ->
                _currentUser.value = me
                _currentUserId.value = me.id
            }
            // Cache-first: getUserDetails returns cached user if already loaded (e.g. from chat list)
            val receiverResult = userRepository.getUserDetails(receiverId)
            if (receiverResult is com.tovtalk.domain.entity.DomainResult.Success) {
                _receiverProfile.value = receiverResult.data
            }

            currentUser?.let { me ->
                val conversationId = if (me.id < receiverId) "${me.id}_$receiverId" else "${receiverId}_${me.id}"
                currentConversationId = conversationId
                messagesJob?.cancel()
                observeMessages(conversationId)
                typingJob?.cancel()
                observeTyping(conversationId, receiverId)
                // Mark as read once when opening chat; do not call on every snapshot (would cause write storm and listener re-fires)
                scope.launch {
                    delay(800)
                    chatRepository.markAsRead(conversationId)
                }
            }
        }
    }

    private fun observeMessages(conversationId: String) {
        messagesJob = scope.launch {
            chatRepository.getMessages(conversationId)
                .onStart { _messages.value = UiState.Loading }
                .catch { e -> _messages.value = UiState.Error(e.message ?: "Failed to load messages") }
                .collect { msgList ->
                    _messages.value = UiState.Success(msgList)
                }
        }
    }

    private fun observeTyping(conversationId: String, receiverId: Int) {
        typingJob = scope.launch {
            chatRepository.observeTypingState(conversationId)
                .catch { e -> 
                    com.tovtalk.utils.logger.Logger.e("Typing error: ${e.message}", "ChatViewModel", e)
                    _isOtherTyping.value = false
                }
                .collect { typingMap ->
                    _isOtherTyping.value = typingMap[receiverId] ?: false
                }
        }
    }

    private var lastSentTypingState: Boolean? = null
    
    fun setTyping(isTyping: Boolean) {
        val convId = currentConversationId ?: return
        if (lastSentTypingState == isTyping) return
        
        lastSentTypingState = isTyping
        scope.launch {
            chatRepository.setTypingState(convId, isTyping)
        }
    }
    
    fun sendTextMessage(receiverId: Int, text: String) {
        if (text.isBlank()) return
        val cur = _currentUser.value
        val rec = _receiverProfile.value
        if (cur?.isReadOnly == true && rec?.isReadOnly == true) return

        _sendMessageError.value = null
        _showKashrutDialog.value = false
        scope.launch {
            setTyping(false)
            when (val result = chatRepository.sendTextMessage(receiverId, text)) {
                is com.tovtalk.domain.entity.DomainResult.Success -> { }
                is com.tovtalk.domain.entity.DomainResult.Error -> {
                    when {
                        result.isKashrutNotAllowed -> {
                            _kashrutDialogMessage.value = result.message
                            _showKashrutDialog.value = true
                        }
                        result.isUserDoesNotAcceptMessages -> {
                            _showUserDoesNotAcceptMessagesDialog.value = true
                        }
                        else -> _sendMessageError.value = result.message
                    }
                }
                is com.tovtalk.domain.entity.DomainResult.Loading -> { }
            }
        }
    }

    fun clearSendMessageError() {
        _sendMessageError.value = null
    }

    fun dismissKashrutDialog() {
        _showKashrutDialog.value = false
        _kashrutDialogMessage.value = null
    }

    fun dismissUserDoesNotAcceptMessagesDialog() {
        _showUserDoesNotAcceptMessagesDialog.value = false
    }

    /**
     * Call when leaving the chat screen (e.g. from DisposableEffect.onDispose).
     * Stops Firestore listeners for messages and typing so they don't keep running in the background.
     */
    fun onClear() {
        setTyping(false)
        messagesJob?.cancel()
        messagesJob = null
        typingJob?.cancel()
        typingJob = null
        currentConversationId = null
    }
}
